import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import {
  calculatePhysicalDimensions,
  detectPhysicalPhotoFormat,
  inspectPhysicalPhoto,
  type DensitySource,
  type PhysicalPhotoInspection,
} from '@/features/physical-photo-size'
import { FilePreflightError } from '@/shared/image/filePreflight'
import { localeCode, useLocale, useT, type Translations } from '@/shared/i18n'
import { ToolHero } from '@/shared/ui/ToolHero'

type Copy = Translations['physicalPhotoSize']
type Status = 'idle' | 'processing' | 'ready' | 'error'

const ANALYTICS_CONTEXT = {
  app: 'imgify',
  domain: 'imgify.ru',
  route: '/razmer-foto-v-sm',
  utility: 'physical-photo-size',
  processingMode: 'local',
  inputMethod: 'upload',
} satisfies ToolAnalyticsContext

const PRESETS = [72, 96, 150, 300, 600] as const

export function PhysicalPhotoSizePage() {
  const t = useT()
  const { locale } = useLocale()
  const numberLocale = localeCode(locale)
  const copy = t.physicalPhotoSize
  const [status, setStatus] = useState<Status>('idle')
  const [result, setResult] = useState<PhysicalPhotoInspection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dropActive, setDropActive] = useState(false)
  const [customPpi, setCustomPpi] = useState('')
  const [overridePpi, setOverridePpi] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const controllerRef = useRef<AbortController | null>(null)
  const generationRef = useRef(0)
  const mountedRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
      controllerRef.current?.abort()
    }
  }, [])

  const reset = useCallback(() => {
    generationRef.current += 1
    controllerRef.current?.abort()
    controllerRef.current = null
    setStatus('idle')
    setResult(null)
    setError(null)
    setCustomPpi('')
    setOverridePpi(null)
  }, [])

  const inspect = useCallback(async (file: File) => {
    const generation = ++generationRef.current
    controllerRef.current?.abort()
    controllerRef.current = null
    let format: ReturnType<typeof detectPhysicalPhotoFormat>
    try {
      format = detectPhysicalPhotoFormat(file)
    } catch (caught) {
      const code = caught instanceof FilePreflightError && caught.code === 'file_too_large'
        ? 'size_limit'
        : 'unsupported_input'
      trackToolRejection(ANALYTICS_CONTEXT, code)
      setStatus('error')
      setResult(null)
      setError(code === 'size_limit' ? copy.errors.tooLarge : copy.errors.unsupported)
      return
    }
    const controller = new AbortController()
    controllerRef.current = controller
    setStatus('processing')
    setResult(null)
    setError(null)
    setCustomPpi('')
    setOverridePpi(null)
    const operation = startToolOperation(ANALYTICS_CONTEXT)
    const startedAt = performance.now()
    try {
      const inspection = await inspectPhysicalPhoto(await file.arrayBuffer(), format, controller.signal)
      if (!mountedRef.current || generation !== generationRef.current) return
      setResult(inspection)
      setStatus('ready')
      operation.success(performance.now() - startedAt)
    } catch (caught) {
      if (!mountedRef.current || generation !== generationRef.current || isAbort(caught)) return
      const code = analyticsError(caught)
      setStatus('error')
      setError(errorMessage(caught, copy))
      operation.error(code)
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null
    }
  }, [copy])

  const effectivePpi = overridePpi !== null
    ? { x: overridePpi, y: overridePpi, userSelected: true }
    : result !== null && result.density.xPpi !== null && result.density.yPpi !== null
      ? { x: result.density.xPpi, y: result.density.yPpi, userSelected: false }
      : null
  const dimensions = useMemo(
    () => result && effectivePpi
      ? calculatePhysicalDimensions(result.width, result.height, effectivePpi.x, effectivePpi.y)
      : null,
    [effectivePpi, result],
  )
  const densityValue = result !== null && result.density.xPpi !== null && result.density.yPpi !== null
    ? `${copy.states[result.density.state]} · ${formatPair(result.density.xPpi, result.density.yPpi, numberLocale)} PPI`
    : result
      ? copy.states[result.density.state] : ''

  const applyCustom = () => {
    const value = Number(customPpi.replace(',', '.'))
    if (!Number.isFinite(value) || value < 1 || value > 2400) {
      setError(copy.errors.ppi)
      return
    }
    setError(null)
    setOverridePpi(value)
  }

  return (
    <div className="mx-auto max-w-6xl">
      <ToolHero
        eyebrow={copy.eyebrow}
        title={copy.title}
        description={copy.description}
        chips={['JPG, PNG, WebP', '50 MB / 36 MP']}
      />

      <Card
        className={cn('mx-auto max-w-3xl transition-colors', dropActive && 'border-primary bg-primary/5')}
        onDragOver={(event) => { event.preventDefault(); setDropActive(true) }}
        onDragLeave={() => setDropActive(false)}
        onDrop={(event) => {
          event.preventDefault()
          setDropActive(false)
          const file = event.dataTransfer.files[0]
          if (file) void inspect(file)
        }}
      >
        <CardContent className="p-4 text-center sm:p-8">
          <div className="mx-auto mb-5 hidden h-16 w-16 place-items-center sm:grid rounded-md border bg-background text-sm font-semibold text-muted-foreground">PPI</div>
          <p className="mb-2 hidden text-lg font-medium [@media(pointer:fine)]:block">{copy.dropTitle}</p>
          <p className="mx-auto mb-5 max-w-xl text-sm leading-6 text-muted-foreground">{copy.dropDescription}</p>
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
            className="hidden"
            aria-label={copy.chooseFile}
            disabled={status === 'processing'}
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void inspect(file)
              event.target.value = ''
            }}
          />
          <div className="flex flex-wrap justify-center gap-3">
            <Button disabled={status === 'processing'} onClick={() => inputRef.current?.click()}>
              {status === 'processing' ? copy.inspecting : copy.chooseFile}
            </Button>
            {status === 'processing' && <Button variant="outline" onClick={reset}>{copy.cancel}</Button>}
            {status !== 'processing' && (result || error) && <Button variant="outline" onClick={reset}>{copy.reset}</Button>}
          </div>
          {error && <p role="alert" className="mx-auto mt-5 max-w-xl rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {result && (
        <div className="mt-8 space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard title={copy.pixelsTitle} value={`${result.width} × ${result.height} px`} description={copy.pixelsDescription} />
            <MetricCard title={copy.densityTitle} value={densityValue} description={copy.stateDescriptions[result.density.state]} />
          </div>

          <Card>
            <CardContent className="p-5 sm:p-6">
              <h2 className="text-xl font-semibold tracking-normal">{copy.sourcesTitle}</h2>
              {result.density.sources.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">{copy.noSources}</p>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full min-w-[34rem] text-left text-sm">
                    <thead><tr className="border-b text-muted-foreground"><th className="pb-2">{copy.source}</th><th className="pb-2">X</th><th className="pb-2">Y</th><th className="pb-2">{copy.unit}</th></tr></thead>
                    <tbody>{result.density.sources.map((source, index) => <SourceRow key={`${source.kind}-${index}`} source={source} copy={copy} />)}</tbody>
                  </table>
                </div>
              )}
              {result.density.state === 'asymmetric' && <p className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">{copy.asymmetricWarning}</p>}
              {result.density.state === 'conflicting_sources' && <p className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">{copy.conflictWarning}</p>}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-5 sm:p-6">
              <h2 className="text-xl font-semibold tracking-normal">{copy.ppiTitle}</h2>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{copy.ppiDescription}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {PRESETS.map((ppi) => (
                  <Button key={ppi} variant={overridePpi === ppi ? 'default' : 'outline'} onClick={() => { setOverridePpi(ppi); setCustomPpi(String(ppi)); setError(null) }}>
                    {ppi} PPI
                  </Button>
                ))}
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <label className="sr-only" htmlFor="physical-photo-ppi">{copy.customPpi}</label>
                <input
                  id="physical-photo-ppi"
                  className="h-10 min-w-0 flex-1 rounded-md border bg-background px-3 text-sm"
                  inputMode="decimal"
                  value={customPpi}
                  placeholder={copy.customPpi}
                  onChange={(event) => setCustomPpi(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') applyCustom() }}
                />
                <Button onClick={applyCustom}>{copy.apply}</Button>
                {result.density.xPpi !== null && result.density.yPpi !== null && overridePpi !== null && (
                  <Button variant="outline" onClick={() => { setOverridePpi(null); setCustomPpi(''); setError(null) }}>{copy.useMetadata}</Button>
                )}
              </div>
              <p className="mt-3 text-xs leading-5 text-muted-foreground">
                {effectivePpi ? (effectivePpi.userSelected ? copy.userSelected : copy.detected) : copy.choosePpi}
              </p>
            </CardContent>
          </Card>

          {dimensions && effectivePpi && (
            <Card>
              <CardContent className="p-5 sm:p-6">
                <h2 className="text-xl font-semibold tracking-normal">{copy.sizeTitle}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{copy.formula}</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                  <SizeValue label={copy.centimeters} value={formatPair(dimensions.centimeters.width, dimensions.centimeters.height, numberLocale)} suffix="cm" />
                  <SizeValue label={copy.millimeters} value={formatPair(dimensions.millimeters.width, dimensions.millimeters.height, numberLocale)} suffix="mm" />
                  <SizeValue label={copy.inches} value={formatPair(dimensions.inches.width, dimensions.inches.height, numberLocale)} suffix="in" />
                </div>
                <p className="mt-4 text-xs leading-5 text-muted-foreground">{copy.calculationNote}</p>
              </CardContent>
            </Card>
          )}

          <section className="grid gap-3 md:grid-cols-3">
            {copy.related.map((item) => <a key={item.href} href={item.href} className="rounded-lg border bg-card p-4 outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"><h2 className="font-semibold">{item.title}</h2><p className="mt-2 text-sm leading-6 text-muted-foreground">{item.description}</p></a>)}
          </section>
        </div>
      )}

      <section className="mt-12 border-t pt-8">
        <h2 className="text-2xl font-semibold tracking-normal">{copy.howToTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-3">{copy.howTo.map((step, index) => <li key={step} className="rounded-lg border bg-card p-4 text-sm leading-6"><strong className="mr-2 text-primary">{index + 1}.</strong>{step}</li>)}</ol>
      </section>
      <section className="mt-10 grid gap-5 border-t pt-8 md:grid-cols-2">{copy.faq.map((item) => <div key={item.question}><h2 className="text-base font-semibold tracking-normal">{item.question}</h2><p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p></div>)}</section>
    </div>
  )
}

function MetricCard({ title, value, description }: { title: string; value: string; description: string }) {
  return <Card><CardContent className="p-5"><h2 className="text-sm font-medium text-muted-foreground">{title}</h2><p className="mt-2 break-words text-2xl font-semibold">{value}</p><p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p></CardContent></Card>
}

function SizeValue({ label, value, suffix }: { label: string; value: string; suffix: string }) {
  return <div className="min-w-0 rounded-lg border bg-background p-4"><p className="text-sm text-muted-foreground">{label}</p><p className="mt-2 break-words text-lg font-semibold tabular-nums">{value} {suffix}</p></div>
}

function SourceRow({ source, copy }: { source: DensitySource; copy: Copy }) {
  const raw = (value: number | null) => value === null ? '—' : Number.isInteger(value) ? String(value) : value.toFixed(4)
  return <tr className="border-b last:border-0"><td className="py-3 pr-3 font-medium">{copy.sourceNames[source.kind]}</td><td className="py-3 pr-3 tabular-nums">{raw(source.rawX)}</td><td className="py-3 pr-3 tabular-nums">{raw(source.rawY)}</td><td className="py-3">{copy.units[source.unit]}</td></tr>
}

function formatPair(width: number, height: number, numberLocale: string): string {
  const formatter = new Intl.NumberFormat(numberLocale, { maximumFractionDigits: 4 })
  return `${formatter.format(width)} × ${formatter.format(height)}`
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

function analyticsError(error: unknown): ToolErrorCode {
  if (error instanceof Error && error.message === 'animated_unsupported') return 'unsupported_input'
  if (error instanceof Error && error.message === 'too_many_pixels') return 'size_limit'
  if (error instanceof Error && error.message === 'invalid_container') return 'invalid_input'
  return 'processing_failed'
}

function errorMessage(error: unknown, copy: Copy): string {
  if (error instanceof Error && error.message === 'animated_unsupported') return copy.errors.animated
  if (error instanceof Error && error.message === 'too_many_pixels') return copy.errors.dimensions
  if (error instanceof Error && error.message === 'invalid_container') return copy.errors.invalid
  return copy.errors.failed
}
